<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '../../lib/i18n';
  import { userApi } from '../../lib/api';
  import { addNotification } from '../../stores/uiStore';
  
  export let userId: number | null = null;
  export let show: boolean = false;
  
  const dispatch = createEventDispatcher();
  
  // Form state
  let username = '';
  let email = '';
  let password = '';
  let role = 'user';
  let loading = false;
  let error = '';
  
  // Watch for changes in userId to load data when editing
  $: if (show && userId !== null) {
    loadUser(userId);
  } else if (show && userId === null) {
    resetForm();
  }
  
  // Load user data for editing
  async function loadUser(id: number) {
    loading = true;
    error = '';
    
    try {
      const user = await userApi.getById(id);
      username = user.username || '';
      email = user.email || '';
      role = user.role || 'user';
      // Don't load password for security reasons
      password = '';
    } catch (err) {
      error = err.message || t('Error loading user');
    } finally {
      loading = false;
    }
  }
  
  // Reset form state
  function resetForm() {
    username = '';
    email = '';
    password = '';
    role = 'user';
    error = '';
  }
  
  // Handle form submission
  async function handleSubmit() {
    loading = true;
    error = '';
    
    try {
      const userData = {
        username,
        email,
        role
      };
      
      // Only include password if it's a new user or the password is changed
      if (password) {
        userData['password'] = password;
      }
      
      let result;
      if (userId === null) {
        // Create new user
        result = await userApi.create(userData);
        addNotification('success', t('User created successfully'));
      } else {
        // Update existing user
        result = await userApi.update(userId, userData);
        addNotification('success', t('User updated successfully'));
      }
      
      // Close the form and notify parent about the update
      closeForm();
      dispatch('refresh');
    } catch (err) {
      error = err.message || t('Error saving user');
    } finally {
      loading = false;
    }
  }
  
  // Close the form
  function closeForm() {
    show = false;
    resetForm();
    dispatch('close');
  }
</script>

{#if show}
  <div class="modal d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {userId === null ? t('Add New User') : t('Edit User')}
          </h5>
          <button type="button" class="btn-close" on:click={closeForm}></button>
        </div>
        <div class="modal-body">
          {#if loading}
            <div class="d-flex justify-content-center">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{t('Loading...')}</span>
              </div>
            </div>
          {:else if error}
            <div class="alert alert-danger">
              {error}
            </div>
          {:else}
            <form on:submit|preventDefault={handleSubmit}>
              <div class="mb-3">
                <label for="username" class="form-label">{t('Username')}</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="username" 
                  bind:value={username} 
                  required
                />
              </div>
              
              <div class="mb-3">
                <label for="email" class="form-label">{t('Email')}</label>
                <input 
                  type="email" 
                  class="form-control" 
                  id="email" 
                  bind:value={email} 
                  required
                />
              </div>
              
              <div class="mb-3">
                <label for="password" class="form-label">
                  {userId === null ? t('Password') : t('Password (leave empty to keep current)')}
                </label>
                <input 
                  type="password" 
                  class="form-control" 
                  id="password" 
                  bind:value={password} 
                  required={userId === null}
                />
              </div>
              
              <div class="mb-3">
                <label for="role" class="form-label">{t('Role')}</label>
                <select class="form-select" id="role" bind:value={role}>
                  <option value="user">{t('User')}</option>
                  <option value="admin">{t('Admin')}</option>
                </select>
              </div>
            </form>
          {/if}
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" on:click={closeForm}>{t('Cancel')}</button>
          <button 
            type="button" 
            class="btn btn-primary" 
            on:click={handleSubmit}
            disabled={loading}
          >
            {loading ? t('Saving...') : t('Save')}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}